import {
    BreadCrumbItem,
    BreadCrumbs as BreadCrumbsComponent,
} from '@eventbrite/breadcrumbs-infra';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

interface CityBrowseBreadCrumbItem {
    clientSideLink?: boolean;
}
interface BreadCrumbContainerProps {
    locationSlug: string;
    currentPlace: string;
    country: string;
    countrySlug: string;
    placeType: string;
    showThingsToDoPhase2: boolean;
    locale: string;
    usesLanguageSubdirectory: boolean;
}

export const BreadCrumbs: React.FunctionComponent<BreadCrumbContainerProps> = (
    props,
) => {
    const {
        locationSlug,
        currentPlace,
        placeType,
        country,
        countrySlug,
        showThingsToDoPhase2,
        locale,
        usesLanguageSubdirectory,
    } = props;

    const isOnline = country ? false : true;

    const breadcrumbItems = React.useMemo<
        BreadCrumbItem<CityBrowseBreadCrumbItem>[]
    >(() => {
        let items: BreadCrumbItem<CityBrowseBreadCrumbItem>[] = [
            {
                value: usesLanguageSubdirectory ? `/${locale}/` : '/',
                display: gettext('Home'),
                clientSideLink: true,
            },
        ];

        if (
            showThingsToDoPhase2 &&
            country &&
            ['locality', 'region'].includes(placeType || '')
        ) {
            items.push({
                value: usesLanguageSubdirectory
                    ? `/${locale}/ttd/${countrySlug}/`
                    : `/ttd/${countrySlug}/`,
                display: gettext('%(country)s', { country }),
            });
        }
        items = [
            ...items,
            ...(showThingsToDoPhase2
                ? [
                      {
                          value: usesLanguageSubdirectory
                              ? `/${locale}/ttd/${locationSlug}/`
                              : `/ttd/${locationSlug}/`,
                          display: isOnline
                              ? gettext('Online')
                              : gettext('Things to do in %(currentPlace)s', {
                                    currentPlace,
                                }),
                      },
                  ]
                : []),
            {
                value: usesLanguageSubdirectory
                    ? `/${locale}/d/${locationSlug}/events/`
                    : `/d/${locationSlug}/events/`,
                display: showThingsToDoPhase2
                    ? isOnline
                        ? gettext('Online Events')
                        : gettext('Events in %(currentPlace)s', {
                              currentPlace,
                          })
                    : currentPlace,
                clientSideLink: true,
            },
        ];

        return items;
    }, [
        locationSlug,
        currentPlace,
        country,
        countrySlug,
        isOnline,
        placeType,
        showThingsToDoPhase2,
        locale,
        usesLanguageSubdirectory,
    ]);

    return (
        <BreadCrumbsComponent
            items={breadcrumbItems}
            separator={
                <span style={{ paddingLeft: '8px', paddingRight: '8px' }}>
                    /
                </span>
            }
            data-testid="city-browse-header"
            shouldIncludeMicroData={true}
            ariaLabel={gettext('breadcrumbs')}
        >
            {(item) =>
                item.clientSideLink ? (
                    <Link to={item.value} {...item.valueMicrodataProps}>
                        {item.displayWithMicroData}
                    </Link>
                ) : (
                    <a href={item.value} {...item.valueMicrodataProps}>
                        {item.displayWithMicroData}
                    </a>
                )
            }
        </BreadCrumbsComponent>
    );
};

interface AppState {
    location: {
        slug: string;
        currentPlace: string;
        country: string;
        countrySlug: string;
        placeType: string;
    };
    app: {
        featureFlags: {
            [key: string]: boolean;
        };
        locale: string;
        uses_language_subdirectory: boolean;
    };
}

export const _mapStateToProps = (state: AppState) => ({
    locationSlug: state.location.slug,
    currentPlace: state.location.currentPlace,
    country: state.location.country,
    countrySlug: state.location.countrySlug,
    placeType: state.location.placeType,
    showThingsToDoPhase2: state.app.featureFlags.enable_things_to_do_phase_2,
    locale: state.app.locale,
    usesLanguageSubdirectory: state.app.uses_language_subdirectory,
});

export const BreadCrumbContainer = connect(_mapStateToProps)(BreadCrumbs);
